import * as React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useBlocker } from "react-router-dom";

/**
 * Warns before unsaved work is thrown away.
 *
 * Wrap a page in <UnsavedChangesProvider pagePath="/manage-league/<id>">. Anything on the page, however
 * deeply nested, reports its own state with useReportUnsaved(key, isDirty, { label, scope }). While at least
 * one thing is dirty:
 *   - closing, refreshing or leaving the tab asks the browser to confirm, and
 *   - moving to another page in the app (a link, a button, the browser's Back/Forward) is held and a dialog asks
 *     whether to stay or leave.
 *
 * Scope decides how far the warning reaches:
 *   "page" (default)  the state lives above the tabs and survives switching tabs, so only leaving the page warns;
 *   "tab"             the state lives inside one tab's content and would be lost by switching tabs, so that warns too.
 *
 * Moves that keep the same address (such as changing ?page=2 on a list) never warn.
 *
 * Needs a data router (createBrowserRouter), which is what lets navigation be held.
 */

const UnsavedChangesContext = React.createContext(null);

/**
 * Whether a navigation should be held, given what is currently dirty. Pure, so it is easy to test.
 * @param {{ pathname: string }} current
 * @param {{ pathname: string }} next
 * @param {string} pagePath the page this guard covers, e.g. "/manage-league/abc"
 * @param {{ scope: "page" | "tab" }[]} dirty
 */
export function shouldHoldNavigation(current, next, pagePath, dirty) {
  if (dirty.length === 0) return false;
  if (current.pathname === next.pathname) return false;
  const stillOnPage = next.pathname === pagePath || next.pathname.startsWith(`${pagePath}/`);
  if (!stillOnPage) return true;
  return dirty.some((d) => d.scope === "tab");
}

export function UnsavedChangesProvider({ pagePath, children }) {
  const sourcesRef = React.useRef(new Map());
  const [version, setVersion] = React.useState(0);

  const setSource = React.useCallback((key, value) => {
    const map = sourcesRef.current;
    const before = map.get(key);
    if (!value) {
      if (!before) return;
      map.delete(key);
    } else {
      if (before && before.label === value.label && before.scope === value.scope) return;
      map.set(key, value);
    }
    setVersion((v) => v + 1);
  }, []);

  // For rendering (the dialog's list, whether to listen for beforeunload). `version` changes whenever the set does.
  const dirty = React.useMemo(() => [...sourcesRef.current.values()], [version]); // eslint-disable-line react-hooks/exhaustive-deps

  // The checks that decide whether to hold a navigation or a page close read the registry itself, not the
  // rendered copy: a click can arrive just after something was saved but before React has re-rendered, and it
  // must see the latest answer, never a stale one.
  const liveDirty = () => [...sourcesRef.current.values()];

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    shouldHoldNavigation(currentLocation, nextLocation, pagePath, liveDirty()),
  );

  // Closing the tab, refreshing, or typing another address: only the browser can ask, with its own wording.
  const anyDirty = dirty.length > 0;
  React.useEffect(() => {
    if (!anyDirty) return undefined;
    const onBeforeUnload = (event) => {
      if (sourcesRef.current.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty]);

  const labels = [...new Set(dirty.map((d) => d.label))];

  return (
    <UnsavedChangesContext.Provider value={setSource}>
      {children}
      <Dialog open={blocker.state === "blocked"} onClose={() => blocker.reset?.()} aria-labelledby="unsaved-changes-title">
        <DialogTitle id="unsaved-changes-title">Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            You have changes that haven&apos;t been saved. If you leave now, they&apos;ll be lost.
            {labels.length > 0 ? (
              <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                {labels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()} autoFocus>
            Stay on this page
          </Button>
          <Button color="error" onClick={() => blocker.proceed?.()}>
            Leave without saving
          </Button>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Reports whether something on the page has unsaved changes. Safe to call from any component under the
 * provider (does nothing outside one). Clears itself when the component goes away.
 *
 * @param {string} key unique per source on the page, e.g. "settings" or "template:GAME_REMINDER"
 * @param {boolean} dirty
 * @param {{ label: string, scope?: "page" | "tab" }} options label is shown in the leave dialog
 */
export function useReportUnsaved(key, dirty, { label, scope = "page" }) {
  const setSource = React.useContext(UnsavedChangesContext);

  React.useEffect(() => {
    if (!setSource) return undefined;
    setSource(key, dirty ? { label, scope } : null);
    return () => setSource(key, null);
  }, [setSource, key, dirty, label, scope]);
}
